import React, { useEffect, useRef, useState, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Loader from "./Loader"; // ✅ import your global Loader

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:1010";

const SpeakerPassModal = ({ price, packageName = "Speaker Pass", onClose }) => {

  const [showPayment, setShowPayment] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const paypalRef = useRef(null);

  const safeOnClose = useCallback(
    (data) => {
      onClose && onClose(data);
    },
    [onClose]
  );

  const validationSchema = Yup.object({
    firstName: Yup.string().required("**First Name is required"),
    lastName: Yup.string().required("**Last Name is required"),
    email: Yup.string().email("**Invalid email").required("**Email is required"),
    topic: Yup.string().required("**Topic is required"), // ✅ make topic mandatory
  });

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (e) => reject(e);
      document.body.appendChild(script);
    });

  const createSpeakerBooking = async (values, paymentMethod = null) => {
    const body = {
      type: "speakerpass",
      speakerpass: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        topic: values.topic,
      package: packageName,

      },
      total: Number(price),
      paymentMethod,
    };

    const res = await axios.post(`${API_BASE}/bookings`, body);
    if (!res.data?.success) throw new Error(res.data?.error || "Failed to create booking");
    return res.data.booking;
  };

  // PayPal Buttons
  useEffect(() => {
    if (selectedGateway !== "paypal" || !booking) return;

    const renderPayPalButtons = () => {
      if (!window.paypal || !paypalRef.current) {
        setError("PayPal SDK not available");
        return;
      }

      paypalRef.current.innerHTML = "";

      window.paypal.Buttons({
        style: { layout: "vertical", color: "gold" },
        createOrder: async () => {
          const resp = await axios.post(`${API_BASE}/payment/paypal/create-order`, {
            bookingId: booking._id,
          });
          return resp.data?.id;
        },
        onApprove: async (data) => {
          setLoading(true);
          try {
            const captureRes = await axios.post(`${API_BASE}/payment/paypal/capture-order`, {
              orderId: data.orderID,
              bookingId: booking._id,
            });
            if (captureRes.data?.success) {
             alert(`✅ Payment successful — ${packageName} confirmed!`);

              safeOnClose({ success: true, bookingId: booking._id });
            } else {
              alert("PayPal verification failed.");
            }
          } finally {
            setLoading(false);
          }
        },
      }).render(paypalRef.current);
    };

    const clientIdEnv =
      process.env.REACT_APP_PAYPAL_CLIENT_ID || process.env.REACT_APP_PAYPAL_KEY_ID || "";
    if (!clientIdEnv) {
      setError("Missing PayPal client id in frontend env (set REACT_APP_PAYPAL_CLIENT_ID).");
      return;
    }

    const currency = booking.currency || "USD";
    const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${clientIdEnv}&currency=${currency}&intent=capture`;

    loadScript(sdkUrl)
      .then(() => renderPayPalButtons())
      .catch(() => setError("Failed to load PayPal SDK"));
  }, [selectedGateway, booking, safeOnClose, packageName]);

  // Razorpay
  const handleRazorpay = async () => {
    try {
      setLoading(true);
      let b = booking;
      if (!b) {
        b = await createSpeakerBooking(payload || {}, "razorpay");
        setBooking(b);
      }

      const orderRes = await axios.post(`${API_BASE}/payment/razorpay/create-order`, {
        bookingId: b._id,
      });
      const order = orderRes.data;

      await loadScript("https://checkout.razorpay.com/v1/checkout.js");

      const amountInPaise = order.amount * (order.currency === "INR" ? 100 : 1);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
        amount: amountInPaise,
        currency: order.currency,
        name: "ARO Events",
        description: `Speaker Pass ${b._id}`,
        order_id: order.id,
        handler: async (response) => {
          setLoading(true);
          try {
            const verifyRes = await axios.post(`${API_BASE}/payment/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              bookingId: b._id,
              amount: amountInPaise,
            });
            if (verifyRes.data?.success) {
              alert("✅ Razorpay payment successful — Speaker Pass confirmed!");
              safeOnClose({ success: true, bookingId: b._id });
            } else {
              alert("❌ Razorpay verification failed!");
            }
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: b.speakerpass.firstName + " " + b.speakerpass.lastName,
          email: b.speakerpass.email,
        },
        modal: {
          ondismiss: () => {
            console.log("❌ Razorpay modal dismissed");
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.message || "Razorpay failed");
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const created = await createSpeakerBooking(values, null);
      setPayload(values);
      setBooking(created);
      setShowPayment(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      {loading && (
        <div style={styles.loaderOverlay}>
          <Loader />
        </div>
      )}
      <motion.div style={styles.modal} initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }}>
        <button style={styles.closeBtn} onClick={() => safeOnClose()} disabled={loading}>
          ✖
        </button>
        <h2 style={styles.heading}>
  {packageName} – USD {price}
</h2>


        <AnimatePresence mode="wait">
          {!showPayment ? (
            <motion.div key="form">
              <Formik
                initialValues={{ firstName: "", lastName: "", email: "", topic: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {() => (
                  <Form style={styles.form}>
                    <Field name="firstName" placeholder="First Name *" style={styles.input} disabled={loading} />
                    <ErrorMessage name="firstName" component="div" style={styles.error} />
                    <Field name="lastName" placeholder="Last Name *" style={styles.input} disabled={loading} />
                    <ErrorMessage name="lastName" component="div" style={styles.error} />
                    <Field name="email" placeholder="Email *" style={styles.input} disabled={loading} />
                    <ErrorMessage name="email" component="div" style={styles.error} />
                    <Field name="topic" placeholder="Topic and company *" style={styles.input} disabled={loading} />
                    <ErrorMessage name="topic" component="div" style={styles.error} />
                    <button type="submit" style={styles.submitBtn} disabled={loading}>
                      {loading ? "Saving..." : "Continue to Payment"}
                    </button>
                  </Form>
                )}
              </Formik>
            </motion.div>
          ) : (
            <motion.div key="payment">
              <h4 style={{ marginBottom: "15px", textAlign: "center" }}>Choose Payment Method</h4>
              {error && <div style={{ color: "red" }}>{error}</div>}
              <div style={styles.paymentOptions}>
                <button onClick={() => setSelectedGateway("paypal")} style={styles.payBtn} disabled={loading}>
                  PayPal
                </button>
                <button onClick={handleRazorpay} style={styles.payBtn} disabled={loading}>
                  Razorpay
                </button>
              </div>
              {selectedGateway === "paypal" && <div ref={paypalRef} style={{ marginTop: 20 }}></div>}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    padding: "30px",
    borderRadius: "16px",
    width: "400px",
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
  },
  closeBtn: { position: "absolute", top: "12px", right: "12px", background: "none", border: "none", fontSize: "18px", cursor: "pointer" },
  heading: { marginBottom: "20px", fontSize: "20px", fontWeight: "600", textAlign: "center" },
  form: { display: "flex", flexDirection: "column", gap: "12px" },
  input: { padding: "12px", borderRadius: "8px", border: "1px solid #ddd" },
  error: { color: "red", fontSize: "12px" },
  submitBtn: {
    background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
  },
  paymentOptions: { display: "flex", gap: "12px", justifyContent: "center", marginTop: "15px" },
  payBtn: { flex: 1, padding: "12px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", border: "1px solid #ccc" },
  loaderOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255,255,255,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
  },
};

export default SpeakerPassModal;
